import { trim } from 'lodash';
import { ReactNode } from 'react';
import { REQUEST_DATE_FORMAT } from '../../../../constants/timeFormat';
import { ApiRequestHelper } from '../../../../helpers/ApiRequest';
import {
  ConsentRequest,
  SignatureCollectionMethodEnum,
  usePatientUpdateConsentForm
} from '../../../../uc-api-sdk';
import { ConsentFormComponent, ConsentFormComponentProps, SubmitValue } from '../../component/ConsentFormComponent/ConsentFormComponent';
import { showConsentFormSentModal } from '../../component/ConsentFormComponent/ConsentFormSentModal';
import { useUpdate } from '../../../../contexts/UpdateContext/UpdateContext';

export interface ConsentFormContainerProps {
  patientId: string;
  initialValues?: Partial<SubmitValue>;
  onSubmit?: (v?: SubmitValue) => void;
  title?: ReactNode;
  onOk?: () => void;
  showConsentAlert?: boolean;
}

export const ConsentFormContainer = ({
  patientId,
  initialValues,
  title,
  onSubmit,
  onOk,
  showConsentAlert
}: ConsentFormContainerProps) => {
  const consentUpdate = usePatientUpdateConsentForm({});
  const signedUpdate = useUpdate('CONSENT_FORM_SIGNED').updateValue;

  const handleSubmit = async (v: SubmitValue) => {
    const sendReq = async () => {
      let userData: ConsentRequest['userData'] | undefined;
      let signedDate: string | undefined;
      if (
        v.signatureMethod === SignatureCollectionMethodEnum.TEAM_MEMBER_DEVICE
        && trim(v.initials)
      ) {
        userData = {
          initials: trim(v.initials),
        };
      }
      if (
        v.signatureMethod === SignatureCollectionMethodEnum.CLINIC_PRINTED
        && v.signDate
      ) {
        signedDate = v.signDate.startOf('day').utc().format(REQUEST_DATE_FORMAT);
      }
      const res = await consentUpdate.send({
        params: {
          memberId: patientId,
          request: {
            consentType: v.signatureMethod,
            userData,
            consentContent: v.consentContent,
            signedDate,
          },
        },
      });
      if (v.signatureMethod === SignatureCollectionMethodEnum.APP) {
        showConsentFormSentModal(onOk);
      }
      onSubmit?.(v);
      signedUpdate();
      return res;
    };
    await ApiRequestHelper.tryCatch(sendReq(), 'Failed to send consent!');
  };

  const processedInitialValues: ConsentFormComponentProps['initialValues'] = {
    signatureMethod: initialValues?.signatureMethod || SignatureCollectionMethodEnum.APP,
  };

  return (
    <ConsentFormComponent
      title={title}
      onSubmit={handleSubmit}
      isLoading={consentUpdate.isLoading}
      initialValues={processedInitialValues}
      patientId={patientId}
      showConsentAlert={showConsentAlert}
    />
  );
};
